import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useMaintenance } from "@/contexts/MaintenanceContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Save, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_MSG = {
  hr: "Trenutno održavamo stranicu. Vraćamo se uskoro.",
  en: "We're performing site maintenance. Back shortly.",
};

export default function AdminMaintenanceCard() {
  const { enabled, message, refresh } = useMaintenance();
  const [draftEnabled, setDraftEnabled] = useState(enabled);
  const [msg, setMsg] = useState(message || DEFAULT_MSG);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Sync local state when context changes (initial load or external refresh).
  useEffect(() => {
    if (!dirty) {
      setDraftEnabled(enabled);
      setMsg(message || DEFAULT_MSG);
    }
  }, [enabled, message, dirty]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/maintenance", {
        enabled: draftEnabled,
        message: {
          hr: (msg?.hr || DEFAULT_MSG.hr).trim(),
          en: (msg?.en || DEFAULT_MSG.en).trim(),
        },
        allow_admin_access: true,
      });
      await refresh();
      setDirty(false);
      toast.success(
        draftEnabled
          ? "Maintenance mode ENABLED — public site shows the maintenance page."
          : "Maintenance mode disabled — public site is live."
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const isLive = !enabled;

  return (
    <section
      className={`rounded-xl p-5 sm:p-6 border ${
        enabled
          ? "border-destructive/50 bg-destructive/5"
          : "border-border bg-card"
      }`}
      data-testid="admin-maintenance-card"
      aria-labelledby="maintenance-card-heading"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full border ${
                isLive
                  ? "border-primary/40 text-primary bg-primary/5"
                  : "border-destructive/50 text-destructive bg-destructive/10"
              }`}
              data-testid="maintenance-status-pill"
            >
              {isLive ? <ShieldCheck className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
              {isLive ? "Live" : "In maintenance"}
            </span>
          </div>
          <h2
            id="maintenance-card-heading"
            className="font-display text-2xl sm:text-3xl mt-3"
          >
            Maintenance mode
          </h2>
          <p className="text-xs text-muted-foreground mt-2 max-w-md">
            While enabled, the public site shows a maintenance page. The admin
            console remains accessible.
          </p>
        </div>
        <label
          className="inline-flex items-center gap-2 cursor-pointer"
          data-testid="maintenance-toggle-wrapper"
        >
          <span className="text-xs text-muted-foreground select-none">
            {draftEnabled ? "ON" : "OFF"}
          </span>
          <Switch
            checked={draftEnabled}
            onCheckedChange={(v) => {
              setDraftEnabled(!!v);
              setDirty(true);
            }}
            data-testid="maintenance-toggle"
            aria-label="Toggle maintenance mode"
          />
        </label>
      </div>

      <Tabs defaultValue="hr" className="mt-5">
        <TabsList data-testid="maintenance-message-tabs">
          <TabsTrigger value="hr" data-testid="maintenance-message-tab-hr">
            HR
          </TabsTrigger>
          <TabsTrigger value="en" data-testid="maintenance-message-tab-en">
            EN
          </TabsTrigger>
        </TabsList>
        <TabsContent value="hr" className="mt-3 space-y-1.5">
          <Label htmlFor="maintenance-msg-hr">Croatian message</Label>
          <Textarea
            id="maintenance-msg-hr"
            rows={3}
            value={msg?.hr || ""}
            onChange={(e) => {
              setMsg({ ...msg, hr: e.target.value });
              setDirty(true);
            }}
            data-testid="maintenance-msg-hr"
          />
        </TabsContent>
        <TabsContent value="en" className="mt-3 space-y-1.5">
          <Label htmlFor="maintenance-msg-en">English message</Label>
          <Textarea
            id="maintenance-msg-en"
            rows={3}
            value={msg?.en || ""}
            onChange={(e) => {
              setMsg({ ...msg, en: e.target.value });
              setDirty(true);
            }}
            data-testid="maintenance-msg-en"
          />
        </TabsContent>
      </Tabs>

      <div className="mt-5 flex items-center justify-end gap-3">
        {dirty && (
          <span className="text-xs text-amber-300" data-testid="maintenance-dirty">
            Unsaved changes
          </span>
        )}
        <Button
          onClick={save}
          disabled={saving || !dirty}
          className={
            draftEnabled
              ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              : "bg-primary text-primary-foreground hover:bg-primary/90"
          }
          data-testid="maintenance-save"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {draftEnabled ? "Save & take site offline" : "Save"}
            </>
          )}
        </Button>
      </div>
    </section>
  );
}
